import type { MenuItem, Order, OrderItem } from '../types';

/**
 * Check if it's a new day compared to the last reset timestamp
 */
export function isNewDay(lastResetTimestamp?: number): boolean {
  if (!lastResetTimestamp) return true;
  
  const lastReset = new Date(lastResetTimestamp);
  const now = new Date();
  
  // Compare dates (ignoring time)
  return (
    lastReset.getFullYear() !== now.getFullYear() ||
    lastReset.getMonth() !== now.getMonth() ||
    lastReset.getDate() !== now.getDate()
  );
}

/**
 * Reset daily availability for menu items that have it enabled
 * This should be called on app initialization and periodically
 */
export function checkAndResetDailyAvailability(items: MenuItem[]): MenuItem[] {
  const now = Date.now();
  const updatedItems: MenuItem[] = [];
  let hasChanges = false;

  for (const item of items) {
    if (item.hasDailyAvailability && item.dailyAvailabilityLimit !== undefined) {
      // Check if we need to reset (new day)
      if (isNewDay(item.lastAvailabilityReset)) {
        updatedItems.push({
          ...item,
          dailyAvailability: item.dailyAvailabilityLimit,
          lastAvailabilityReset: now,
        });
        hasChanges = true;
      } else {
        updatedItems.push(item);
      }
    } else {
      updatedItems.push(item);
    }
  }

  return hasChanges ? updatedItems : items;
}

/**
 * Get the start of today in milliseconds (midnight)
 */
export function getTodayStart(): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.getTime();
}

/**
 * Calculate the total reserved quantity for a menu item from active orders
 * Active orders are those that are not yet fully served (status !== 'served' or items not all served)
 */
export function calculateReservedQuantity(itemId: number, orders: Order[]): number {
  let reserved = 0;
  
  for (const order of orders) {
    // Skip orders that are fully served
    if (order.status === 'served') continue;
    
    // Count items in this order that match the itemId and are not yet served
    for (const orderItem of order.items) {
      if (orderItem.id === itemId && !orderItem.served) {
        reserved += orderItem.quantity;
      }
    }
  }
  
  return reserved;
}

/**
 * Get the available quantity for a menu item (dailyAvailability - reservedQuantity)
 * This is the quantity that can still be ordered
 */
export function getAvailableQuantity(item: MenuItem, orders: Order[]): number {
  if (!item.hasDailyAvailability) {
    // If daily availability is not enabled, return a large number (effectively unlimited)
    return 999999;
  }
  
  const dailyAvailability = item.dailyAvailability ?? 0;
  const reserved = calculateReservedQuantity(item.id, orders);
  const available = dailyAvailability - reserved;
  
  return Math.max(0, available);
}

